import { StepName } from '~/core/enums';
import useAppliance from '~/core/hooks/useAppliance';
import { ApplianceType } from '~/core/models';
import { useDefineLoadEstimateCode } from '~/legacy/easy-estimator-page/context/estimateContext/hooks';
import { useAppDispatch } from '~/redux/hooks';
import { resetSelectedColorId } from '~/redux/slices/color';
import {
  resetSelectedBrowsingOptionAndCookingApplianceId,
  resetSelectedCookingAndRanges,
} from '~/redux/slices/cookingAppliance';
import { resetSelectedCornerRadius } from '~/redux/slices/cornerRadii';
import { resetSelectedCustomSide } from '~/redux/slices/customSide';
import { setApplianceCutouts, setCustomCutouts } from '~/redux/slices/cutout';
import { resetSelectedDiagonalCorner } from '~/redux/slices/diagonalCorner';
import { resetSelectedEdge } from '~/redux/slices/edge';
import {
  resetEstimate,
  updateCurrentEstimateCode,
  updateIsNewEstimate,
  updateLoadEstimateCode,
  updateShowListOfEstimates,
} from '~/redux/slices/estimate';
import { resetSelectedMaterial } from '~/redux/slices/material';
import {
  clearSelectedWarrantyProduct,
  resetCustomLaborOptionalItems,
  resetOptionalItems,
} from '~/redux/slices/optionalItems';
import {
  resetAllPricing as resetAllPricingRedux,
  resetCountertopAreaQuantity,
} from '~/redux/slices/pricedProducts';
import {
  clearSelectedDrawingTabs,
  resetSelectedPredefinedShape,
  updateSelectedDrawingObjectName,
} from '~/redux/slices/shape';
import {
  resetAllSinkQuantities,
  resetSelectedBrowsingOption,
  resetSelectedSink,
} from '~/redux/slices/sink';
import { resetAllFilters } from '~/redux/slices/sortAndFilter';
import {
  enableStep,
  getStepState,
  goToStep,
  resetStepper,
  setAllowAutoNavigation,
} from '~/redux/slices/stepper';
import { updateSelectedStore } from '~/redux/slices/store';
import { resetSelectedTearOuts } from '~/redux/slices/tearOut';
import { resetVisitedSteps } from '~/redux/slices/visitedSteps';
import { resetZipcode } from '~/redux/slices/zipcode';
import {
  useChipMinimizerContext,
  useDishwasherContext,
  useDrawingShapeContext,
  useFaucetContext,
  usePricedProductsContext,
  useUndoStackContext,
} from '~contexts';

interface ResetOptions {
  allowZipcodeReset?: boolean;
  allowStoreReset?: boolean;
  allowMaterialReset?: boolean;
}

export const useResetSteps = () => {
  const dispatch = useAppDispatch();

  const {
    handlers: { resetDrawingShapes, resetActiveShape },
  } = useDrawingShapeContext();

  const { clearUndoStack } = useUndoStackContext();

  const { resetPricedProducts } = usePricedProductsContext();
  const { resetFaucets } = useFaucetContext();
  const { resetDishwashers } = useDishwasherContext();

  const { resetSelectedChipMinimizers } = useChipMinimizerContext();

  const { removeLoadEstimateCode } = useDefineLoadEstimateCode();

  const { deleteAllAppliancesByType } = useAppliance();

  const resetDesignAreaSelectionsState = () => {
    // Shape
    dispatch(resetCountertopAreaQuantity());
    dispatch(updateSelectedDrawingObjectName(''));
    dispatch(clearSelectedDrawingTabs());
    dispatch(resetSelectedPredefinedShape());

    // Edge
    dispatch(resetSelectedEdge());

    // CornerRadius
    dispatch(resetSelectedCornerRadius());

    // DiagonalCorner
    dispatch(resetSelectedDiagonalCorner());

    // CustomSide
    dispatch(resetSelectedCustomSide());

    // Sinks
    dispatch(resetSelectedBrowsingOption());
    dispatch(resetSelectedSink());
    dispatch(resetAllSinkQuantities());
    resetSelectedChipMinimizers();
    deleteAllAppliancesByType([ApplianceType.SINK, ApplianceType.SINK_LABOR]);

    // Cooking appliances
    dispatch(resetSelectedBrowsingOptionAndCookingApplianceId());
    dispatch(resetSelectedCookingAndRanges());
    deleteAllAppliancesByType([ApplianceType.COOKTOP, ApplianceType.RANGE]);

    // Cutouts
    dispatch(setCustomCutouts());
    dispatch(setApplianceCutouts());

    // TearOuts
    dispatch(resetSelectedTearOuts());

    // Addons
    dispatch(clearSelectedWarrantyProduct());
    dispatch(resetCustomLaborOptionalItems());
    dispatch(resetOptionalItems());
    resetDishwashers();
    resetFaucets();

    // other things
    dispatch(resetAllFilters());
    clearUndoStack();
    resetActiveShape();
    resetDrawingShapes();
  };

  const resetPrimarySelectionsState = (options: ResetOptions) => {
    if (options.allowZipcodeReset) {
      dispatch(resetZipcode());
    }
    if (options.allowStoreReset) {
      dispatch(updateSelectedStore(undefined));
    }
    if (options.allowMaterialReset) {
      dispatch(resetSelectedMaterial());
    }
    dispatch(resetSelectedColorId());
  };

  const resetEstimateState = () => {
    resetPricedProducts();
    dispatch(resetAllPricingRedux());
    removeLoadEstimateCode();
    dispatch(updateIsNewEstimate(true));
    dispatch(updateLoadEstimateCode(undefined));
    dispatch(updateCurrentEstimateCode(''));
    dispatch(updateShowListOfEstimates(false));
    resetEstimate();
  };

  const resetStepperState = (stepName: StepName) => {
    const step = getStepState(stepName);

    dispatch(resetStepper(stepName));
    dispatch(resetVisitedSteps(step.next));
    dispatch(enableStep(step.current));
    dispatch(goToStep(stepName));
  };

  const resetEasyEstimatorState = (
    stepName:
      | StepName.store
      | StepName.material
      | StepName.color = StepName.store,
    options: ResetOptions = {
      allowZipcodeReset: true,
      allowStoreReset: true,
      allowMaterialReset: true,
    }
  ) => {
    resetDesignAreaSelectionsState();
    resetPrimarySelectionsState(options);
    resetEstimateState();
    resetStepperState(stepName);
  };

  const restoreDataWithoutAutoNavigation = (callback: () => unknown) => {
    dispatch(setAllowAutoNavigation(false));
    callback();
    dispatch(setAllowAutoNavigation(true));
  };

  return { resetEasyEstimatorState, restoreDataWithoutAutoNavigation };
};
